import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { MapPin, Mail } from 'lucide-react';

export const Footer = () => {
  const { language } = useLanguage();
  const location = useLocation();

  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const address = "Via XI Traversa n.68, 95032, Belpasso (CT)";
  const email = "curtigghiu.bnb@gmail.com";
  const mapsQuery = encodeURIComponent("Via XI Traversa 68, Belpasso, Catania");
  const mapsEmbed = `https://maps.google.com/maps?q=${mapsQuery}&t=&z=16&ie=UTF8&iwloc=&output=embed`;
  const mapsLink = `https://maps.google.com/?q=${mapsQuery}`;

  return (
    <footer className="bg-[#2C302E] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-4 mb-4">
              <img 
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/zk5klw3b_TARGA1.webp"
                alt="Curtigghiu Logo"
                className="w-14 h-14 object-contain rounded-md bg-white p-1"
              />
              <h3 
                className="text-5xl leading-none text-white"
                style={{fontFamily: '"Dancing Script", cursive', fontWeight: 600}}
              >
                Curtigghiu
              </h3>
            </div>
            <p 
              className="text-xs uppercase tracking-[0.3em] text-white/60 mb-6"
              style={{fontFamily: 'Manrope, sans-serif'}}
            >
              Bed & Breakfast
            </p>
            <p 
              className="text-white/70 leading-relaxed text-sm mb-4"
              style={{fontFamily: 'Manrope, sans-serif'}}
            >
              {language === 'it' 
                ? 'Un\'oasi di tranquillità nel cuore di Belpasso, dove tradizione siciliana e design contemporaneo si incontrano.'
                : 'An oasis of tranquility in the heart of Belpasso, where Sicilian tradition meets contemporary design.'}
            </p>
            <div className="space-y-1 pt-4 border-t border-white/10">
              <p className="text-xs text-white/50" style={{fontFamily: 'Manrope, sans-serif'}}>
                <span className="text-white/70 font-medium">CIR:</span> 19087007C163455
              </p>
              <p className="text-xs text-white/50" style={{fontFamily: 'Manrope, sans-serif'}}>
                <span className="text-white/70 font-medium">CIN:</span> IT087007C1KF4MXZBS
              </p>
            </div>
          </div>

          {/* Contacts */}
          <div>
            <p 
              className="text-xs uppercase tracking-[0.3em] text-[#8A9A86] mb-6"
              style={{fontFamily: 'Manrope, sans-serif'}}
            >
              {language === 'it' ? 'Contatti' : 'Contact'}
            </p>
            
            <div className="space-y-5">
              <a 
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-address-link"
                className="flex items-start gap-4 text-white/80 hover:text-white transition-colors group"
              >
                <MapPin size={20} className="text-[#8A9A86] mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span 
                  className="text-sm leading-relaxed"
                  style={{fontFamily: 'Manrope, sans-serif'}}
                >
                  {address}
                </span>
              </a>

              <a 
                href={`mailto:${email}`}
                data-testid="footer-email-link"
                className="flex items-center gap-4 text-white/80 hover:text-white transition-colors group"
              >
                <Mail size={20} className="text-[#8A9A86] flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span 
                  className="text-sm"
                  style={{fontFamily: 'Manrope, sans-serif'}}
                >
                  {email}
                </span>
              </a>
            </div>
          </div>

          {/* Map */}
          <div>
            <p 
              className="text-xs uppercase tracking-[0.3em] text-[#8A9A86] mb-6"
              style={{fontFamily: 'Manrope, sans-serif'}}
            >
              {language === 'it' ? 'Come Raggiungerci' : 'How to Find Us'}
            </p>
            <div 
              className="rounded-lg overflow-hidden h-56 border border-white/10"
              data-testid="footer-map"
            >
              <iframe
                title="Curtigghiu location"
                src={mapsEmbed}
                width="100%"
                height="100%"
                style={{border: 0}}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p 
            className="text-xs text-white/50"
            style={{fontFamily: 'Manrope, sans-serif'}}
          >
            © {new Date().getFullYear()} Curtigghiu B&B — {language === 'it' ? 'Tutti i diritti riservati' : 'All rights reserved'}
          </p>
          <p 
            className="text-xs text-white/40"
            style={{fontFamily: 'Manrope, sans-serif'}}
          >
            Belpasso, Sicilia
          </p>
        </div>
      </div>
    </footer>
  );
};
